import { FC, useState } from 'react';
import { ArrowBackIos } from '@mui/icons-material';
import { IconButton, Skeleton, Stack, Typography } from '@mui/material';

import { PAYMENT_STEP } from '~common';
import { MainButton } from '~components/atoms';
import { PassHeroCard } from '~components/atoms/pass/PassHeroCard';
import { PassNoticeBanner } from '~components/atoms/pass/PassNoticeBanner';
import { PassUsageStats } from '~components/atoms/pass/PassUsageStats';
import {
  CancelPassDialog,
  PassResultDialog,
  ResubscribeDialog,
} from '~components/molecules/pass/PassActionDialogs';
import { LocalizationTypography } from '~components/organisms/localization';
import {
  useMutationUpdateUserPassStatus,
  useQueryGetUserPassDetail,
} from '~hooks';
import {
  useLanguageContext,
  usePaymentStepContext,
  useStationPricingContext,
  useUserSubscriptionContext,
} from '~providers';
import {
  SubscriptionStatus,
  UserPassAction,
  UserPassState,
} from '~types/subscription.type';
import { formatCurrency, getCardImage, properCardBrandName } from '~utils';
import {
  findLivePeriod,
  getBillingCycleLabel,
  isPassCancellable,
  isPassResubscribable,
  isSwapLimitReached,
} from '~utils/pass.util';

type ResultDialog = 'cancelled' | 'reactivated' | null;

const DetailRow: FC<{
  labelId: string;
  label: string;
  children: React.ReactNode;
}> = ({ labelId, label, children }) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    gap={2}
    sx={{ py: 1 }}
  >
    <LocalizationTypography
      textId={labelId}
      defaultText={label}
      variant="Poppins/Body-md"
      color="#6B7280"
    />
    {children}
  </Stack>
);

/**
 * The "Rental Pass" screen: the pass, what's left of the current cycle, and the
 * cancel / un-cancel controls.
 *
 * It reads the pass from `/api/user-pass/detail` for the card and the billing
 * cycle, and the running period from `/api/subscription/current` for swap
 * counts — the detail response's newest period is the same row, but `current`
 * is what the rest of the flow is already gated on.
 */
export const PassDetailStep: FC = () => {
  const { lt, locale } = useLanguageContext();
  const { handleSetPaymentStep } = usePaymentStepContext();
  const {
    activePass,
    activeSubscription,
    subscriptionStatus,
    refetchSubscription,
  } = useUserSubscriptionContext();
  const { stationPricing, hasMultiplePricingOptions } =
    useStationPricingContext();

  const [confirmAction, setConfirmAction] = useState<UserPassAction | null>(
    null,
  );
  const [resultDialog, setResultDialog] = useState<ResultDialog>(null);

  const { data: passDetail, isFetching } = useQueryGetUserPassDetail(
    activePass?.id,
  );

  const updatePassStatus = useMutationUpdateUserPassStatus({
    onSuccess: (pass) => {
      setConfirmAction(null);
      setResultDialog(
        pass.state === UserPassState.CANCELED ? 'cancelled' : 'reactivated',
      );
      refetchSubscription();
    },
  });

  // `SELECT_PRICING_MODELS` renders nothing when the station has a single
  // option, so it is only a valid destination while there is something to pick
  // between. Landing there anyway strands the user on an empty screen whose
  // only link leads back here.
  const goBack = () => {
    if (activeSubscription) {
      handleSetPaymentStep(PAYMENT_STEP.AVAILABLE_SWAPS);
      return;
    }

    handleSetPaymentStep(
      hasMultiplePricingOptions
        ? PAYMENT_STEP.SELECT_PRICING_MODELS
        : PAYMENT_STEP.RENT_A_CHARGER,
    );
  };

  if (!activePass) {
    return null;
  }

  const pass = passDetail?.pass ?? activePass;
  const payment = passDetail?.payment;

  const isActive = pass.state === UserPassState.ACTIVE;
  const isCanceled = pass.state === UserPassState.CANCELED;
  // The renewal charge has not landed yet. Read off the backend's own verdict
  // rather than re-derived from period states: through a retry the previous
  // period sits in `RENEWING`, which the backend counts as ongoing but not
  // usable, so a client-side state list read it as a live cycle and never
  // showed this case.
  const paymentIssue =
    isActive && subscriptionStatus === SubscriptionStatus.RENEWAL_PENDING;

  // `activeSubscription` wins when present because it is the station-checked
  // answer. It is null for `NOT_USABLE_AT_STATION` too, where a live period
  // does exist, hence the fallback to the detail's periods — matched on state,
  // never on position, since the newest can be an unpaid `REQUESTED` renewal.
  // While a renewal is pending there is no paid cycle to report at all.
  const livePeriod = findLivePeriod(passDetail?.periods);
  const entitlement = paymentIssue ? null : activeSubscription ?? livePeriod;

  const endAt = entitlement?.endAt ?? null;
  const usedChargers = entitlement?.usedChargers ?? 0;
  const swapsLeft = entitlement?.swapsLeft ?? -1;
  const maxSwaps = entitlement?.maxSwaps ?? -1;
  // The server refuses CANCEL while a renewal is in flight or has failed, so
  // offering the control there would hand the user a button that only errors.
  const cancellable = isPassCancellable(passDetail?.periods);
  // The server only un-cancels while the latest period's paid window is still
  // open. Once it has run out — a battery kept past the end leaves the period
  // OVERDUE and the pass still CANCELED — the button would only error.
  const resubscribable =
    isCanceled && isPassResubscribable(passDetail?.periods);
  // `name` is nullable on the wire — a pass whose pricing no longer resolves
  // has no title, and the dialogs read better with the generic noun than a gap.
  const passName =
    pass.name ?? lt('app.rentalPage.newStepThree.pass.title', 'Rental Pass');
  const limitReached =
    !!entitlement && isSwapLimitReached({ maxSwaps, swapsLeft });

  const formattedDate = (value?: string | null) =>
    value
      ? new Date(value).toLocaleDateString(locale, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      : '';

  // `/user-pass/detail` wraps the pass in `WithCurrencySymbol`, which unwraps
  // onto the pass itself. `/api/subscription/current` does not wrap, so the
  // fallback pass from there has no symbol and the station's is used instead.
  const currencySymbol =
    pass.symbol ?? pass.pricing?.symbol ?? stationPricing?.symbol;
  const cyclePrice = formatCurrency(pass.pricing?.price, currencySymbol, 2);
  const cycleLabel = getBillingCycleLabel(pass.pricing, lt);
  // `pass.pricing` is the ongoing price. An invoice reflects whatever promotion
  // applied to that period, so a discounted first period would otherwise read
  // as the recurring price.
  const recurringPrice = cycleLabel
    ? `${cyclePrice} / ${cycleLabel}`
    : cyclePrice;

  return (
    <Stack gap={3} sx={{ pb: 1.5 }}>
      <Stack direction="row" alignItems="center">
        <IconButton
          aria-label="back"
          size="small"
          color="inherit"
          sx={{ paddingY: 0 }}
          onClick={goBack}
        >
          <ArrowBackIos />
        </IconButton>
        <LocalizationTypography
          textId="app.rentalPage.newStepThree.pass.title"
          defaultText="Rental Pass"
          variant="Poppins/Heading-3"
        />
      </Stack>

      {isFetching && !passDetail ? (
        <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 2 }} />
      ) : (
        <PassHeroCard
          state={pass.state}
          endAt={endAt}
          paymentIssue={paymentIssue}
        />
      )}

      {/* No live period means no allowance to report. Rendering the tiles
          anyway would put a swap count on a cycle nobody has paid for. */}
      {entitlement && (
        <PassUsageStats
          usedChargers={usedChargers}
          swapsLeft={swapsLeft}
          muted={!isActive}
        />
      )}

      {/* Only a recurring pass has a next billing date to reset on; a one-off
          pass's allowance is simply spent. */}
      {limitReached && isActive && (
        <PassNoticeBanner>
          {pass.autoRenew ? (
            <LocalizationTypography
              textId="app.rentalPage.newStepThree.pass.limitReached"
              defaultText="You’ve used all your swaps for this cycle. Your swaps will reset on your next billing date."
              variant="Poppins/Body-md"
              color="inherit"
            />
          ) : (
            <LocalizationTypography
              textId="app.rentalPage.newStepThree.pass.limitReachedNoRenewal"
              defaultText="You’ve used all your swaps for this period."
              variant="Poppins/Body-md"
              color="inherit"
            />
          )}
        </PassNoticeBanner>
      )}

      <Stack>
        {/* A one-off pass has no next billing date to report, ever. Showing the
            row with a dash invited the old bug where "— (Cancelled)" was
            printed for a pass nobody had cancelled. */}
        {pass.autoRenew && (
          <DetailRow
            labelId="app.rentalPage.newStepThree.pass.nextBillingDate"
            label="Next Billing Date"
          >
            {paymentIssue ? (
              <LocalizationTypography
                textId="app.rentalPage.newStepThree.pass.nextBillingRetrying"
                defaultText="— (Retrying)"
                variant="Poppins/Subheading-md"
                color="#111928"
              />
            ) : isCanceled ? (
              <LocalizationTypography
                textId="app.rentalPage.newStepThree.pass.noNextBilling"
                defaultText="— (Cancelled)"
                variant="Poppins/Subheading-md"
                color="#111928"
              />
            ) : (
              // Anything else keeps a neutral dash: only a cancellation earns
              // the word "cancelled".
              <Typography variant="Poppins/Subheading-md" color="#111928">
                {endAt ? formattedDate(endAt) : '—'}
              </Typography>
            )}
          </DetailRow>
        )}

        {payment?.cardLast4 && (
          <DetailRow
            labelId="app.rentalPage.newStepThree.pass.paymentMethod"
            label="Payment Method"
          >
            <Stack direction="row" alignItems="center" gap={1}>
              <img
                src={getCardImage('CREDIT_CARD', payment.cardBrand ?? '')}
                alt={payment.cardBrand ?? 'card'}
                width={28}
              />
              <Typography variant="Poppins/Subheading-md" color="#111928">
                {`${properCardBrandName(payment.cardBrand ?? '')} •••• ${
                  payment.cardLast4
                }`}
              </Typography>
            </Stack>
          </DetailRow>
        )}

        {/* "Cycle" only means something for a pass that recurs; on a one-off
            pass the same figure is a validity period. */}
        <DetailRow
          labelId={
            pass.autoRenew
              ? 'app.rentalPage.newStepThree.pass.billingCycle'
              : 'app.rentalPage.newStepThree.pass.validFor'
          }
          label={pass.autoRenew ? 'Cycle' : 'Valid for'}
        >
          <Typography variant="Poppins/Subheading-md" color="#111928">
            {recurringPrice}
          </Typography>
        </DetailRow>
      </Stack>

      {/* `RENEW` is un-cancel, and only works while the paid period is still
          current. An ENDED pass restarts through the normal purchase flow, so
          neither control belongs on it. */}
      {isActive &&
        pass.autoRenew &&
        (cancellable ? (
          <MainButton
            variant="text"
            color="error"
            onClick={() => setConfirmAction(UserPassAction.CANCEL)}
          >
            <LocalizationTypography
              textId="app.rentalPage.newStepThree.pass.cancelPass"
              defaultText="Cancel Pass"
              variant="Poppins/Subheading-md"
              color="#EF4444"
            />
          </MainButton>
        ) : (
          // Say why the control is missing — a button that silently disappears
          // reads as a bug rather than as a rule.
          <LocalizationTypography
            textId="app.rentalPage.newStepThree.pass.cancelUnavailable"
            defaultText="You can cancel once your current billing period settles."
            variant="Poppins/Body-sm"
            color="#6B7280"
            textAlign="center"
          />
        ))}

      {resubscribable && (
        <MainButton
          variant="contained"
          color="primary"
          onClick={() => setConfirmAction(UserPassAction.RENEW)}
        >
          <LocalizationTypography
            textId="app.rentalPage.newStepThree.pass.resubscribe"
            defaultText="Resubscribe"
            variant="Poppins/Subheading-md"
          />
        </MainButton>
      )}

      <CancelPassDialog
        open={confirmAction === UserPassAction.CANCEL}
        onClose={() => setConfirmAction(null)}
        onConfirm={() =>
          updatePassStatus.mutate({
            passId: pass.id,
            action: UserPassAction.CANCEL,
          })
        }
        isLoading={updatePassStatus.isLoading}
        passName={passName}
        accessUntil={endAt ? formattedDate(endAt) : undefined}
      />

      <ResubscribeDialog
        open={confirmAction === UserPassAction.RENEW}
        onClose={() => setConfirmAction(null)}
        onConfirm={() =>
          updatePassStatus.mutate({
            passId: pass.id,
            action: UserPassAction.RENEW,
          })
        }
        isLoading={updatePassStatus.isLoading}
        passName={passName}
        price={recurringPrice}
      />

      <PassResultDialog
        open={resultDialog !== null}
        onClose={() => {
          setResultDialog(null);
          goBack();
        }}
        passName={passName}
        accessUntil={endAt ? formattedDate(endAt) : undefined}
        variant={resultDialog ?? 'cancelled'}
      />
    </Stack>
  );
};
